import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { Grid, TextField, units } from '@hbf/dsl';

interface Props {
  labelMin?: string;
  valueMin?: number | string;
  onMinChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMinBlur: () => void;
  labelMax?: string;
  valueMax?: number | string;
  onMaxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMaxBlur: () => void;
  suffix?: string;
  prefix?: string;
  id?: string;
}

const useStyles = makeStyles()(theme => ({
  inputsContainer: {
    marginTop: units.rem(1),
  },
  input: {
    '&::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '&::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    MozAppearance: 'textfield',
  },
  inputWrapper: {
    flexGrow: 1,
  },
  minInputWrapper: {
    marginRight: theme.spacing(2),
  },
  adornment: {
    color: theme.palette.grey[500],
    marginRight: theme.spacing(0.5),
  },
  labelRoot: {
    whiteSpace: 'nowrap',
  },
}));

const CommonMinMaxNumericInput: React.FC<Props> = ({
  id,
  labelMin,
  valueMin,
  onMinChange,
  onMinBlur,
  labelMax,
  valueMax,
  onMaxChange,
  onMaxBlur,
  suffix,
  prefix,
}) => {
  const { classes, cx } = useStyles();

  const [minValue, setMinValue] = React.useState(valueMin);
  const [maxValue, setMaxValue] = React.useState(valueMax);

  React.useEffect(() => {
    setMinValue(valueMin);
  }, [valueMin]);

  const onMinInputBlur = React.useCallback(
    e => {
      if (e.target.value === '') {
        setMinValue('0');
      }
      onMinBlur();
    },
    [onMinBlur],
  );

  const onMinInputFocus = React.useCallback(e => {
    if (e.target.value === '' || e.target.value === '0') {
      setMinValue('');
    }
  }, []);

  React.useEffect(() => {
    setMaxValue(valueMax);
  }, [valueMax]);

  const onMaxInputBlur = React.useCallback(
    e => {
      if (e.target.value === '') {
        setMaxValue('0');
      }
      onMaxBlur();
    },
    [onMaxBlur],
  );

  const onMaxInputFocus = React.useCallback(e => {
    if (e.target.value === '' || e.target.value === '0') {
      setMaxValue('');
    }
  }, []);

  return (
    <Grid
      container
      className={classes.inputsContainer}
      wrap="nowrap"
      justifyContent="space-between"
    >
      <Grid item className={cx(classes.inputWrapper, classes.minInputWrapper)}>
        <TextField
          fullWidth
          type="number"
          id={`${id}Min`}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            className: classes.input,
          }}
          InputProps={{
            ...(suffix && {
              endAdornment: <span className={classes.adornment}>{suffix}</span>,
            }),
            ...(prefix && {
              startAdornment: (
                <span className={classes.adornment}>{prefix}</span>
              ),
            }),
          }}
          InputLabelProps={{
            classes: { root: classes.labelRoot },
          }}
          label={labelMin}
          value={minValue}
          onChange={onMinChange}
          onBlur={onMinInputBlur}
          onFocus={onMinInputFocus}
        />
      </Grid>
      <Grid item className={classes.inputWrapper}>
        <TextField
          fullWidth
          type="number"
          id={`${id}Max`}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            className: classes.input,
          }}
          InputProps={{
            ...(suffix && {
              endAdornment: <span className={classes.adornment}>{suffix}</span>,
            }),
            ...(prefix && {
              startAdornment: (
                <span className={classes.adornment}>{prefix}</span>
              ),
            }),
          }}
          InputLabelProps={{
            classes: { root: classes.labelRoot },
          }}
          label={labelMax}
          value={maxValue}
          onChange={onMaxChange}
          onBlur={onMaxInputBlur}
          onFocus={onMaxInputFocus}
        />
      </Grid>
    </Grid>
  );
};

export { CommonMinMaxNumericInput };
